const last = require('lodash/last');
const isString = require('lodash/isString');
const isNumber = require('lodash/isNumber');
const isArray = require('lodash/isArray');

const split = require('./split');
const join = require('./join');

const WILDCARD = '*';
const OPEN_VAR = '{';
const CLOSE_VAR = '}';
const OPEN_INDEX = '[';
const CLOSE_INDEX = ']';
const ESCAPE_QUOTE = '"';
const STRING_TYPE = 'keys';
const NUMBER_TYPE = 'integers';
const TYPE_SEPARATOR = ':';

/**
 * A string or falcor path array describing exactly one leaf in the object graph.
 * May not use range expressions, variables, wildcards or property arrays.
 * @typedef {(string|string[])} LeafPath
 * @example
 * 'fx.pairs[0].bid'
 *  ['fx', 'pairs', 0, 'bid']
 */
/**
 * A string or array of strings describing a route to be queried.
 * This can use range expressions (`[0..20]`) and property arrays
 * (`["bid, "ask", "name", "type"]`)
 * @typedef {(string|string[])} QueryPathSet
 * @example
 * 'fx.pairs[0].bid'
 * 'fx.pairs[0..10].bid'
 * 'fx.pairs[0..10]["bid, "ask", "name", "type"]'
 */
/**
 * A pair expressing a path and its value
 * @typedef {Object} PathValue
 * @property {string|Array<string>} path path or Falcor-style array path expression
 * @property {*} value the value
 * @example
 * { path: 'fx.pairs[0].bid', value: 1.444330 }
 * { path: ['fx', 'pairs', 0, 'bid'], value: 1.444330 }
 */
/**
 * A string or array of strings describing a route to be handled.
 * This can use wildcard operators (`*`), variables (`{integers}` or `{keys}`)
 * or arrays of matched properties
 * @typedef {(string|string[])} RoutePathSet
 * @example
 * 'fx.*'
 * 'fx.pairs[{integers}].{keys}'
 * 'fx.pairs[0].["bid","ask"]'
 */
/**
 * An object containing a route matched to a path, along with the
 * interpolated parameters and their values
 * @typedef {Object} RoutePathAndParams
 * @property {RoutePathSet} routePath the matching route path
 * @property {Object<string, *>} params any interpolated parameters
 * @example
 * // matching against 'fx.pairs[0].bid'
 * { routePath: 'fx.pairs[{integers:id}].bid', params: { id: 0 } }
 */

function escapeToPathSyntax(path) {
  const openRegex = new RegExp(`\\${OPEN_INDEX}\\${OPEN_VAR}`, 'g');
  const closeRegex = new RegExp(`\\${CLOSE_VAR}\\${CLOSE_INDEX}`, 'g');

  const pathEscaped = path
    .replace(openRegex, OPEN_INDEX + ESCAPE_QUOTE + OPEN_VAR)
    .replace(closeRegex, CLOSE_VAR + ESCAPE_QUOTE + CLOSE_INDEX);
  return split(pathEscaped);
}

function matchesLengthOrWildcard(queryPathArray, routePathArray) {
  return (
    queryPathArray.length === routePathArray.length ||
    isWildCardKey(last(routePathArray))
  );
}

function isWildCardKey(routeKey) {
  return routeKey === WILDCARD;
}

function matchAgainstVariable(queryKey, routeKey) {
  let varType = routeKey.substring(1, routeKey.length - 1);
  let varName = '';
  if (varType.includes(TYPE_SEPARATOR)) {
    [varType, varName] = varType.split(TYPE_SEPARATOR);
  }
  if (
    (varType === STRING_TYPE && !isString(queryKey)) ||
    (varType === NUMBER_TYPE && !isNumber(queryKey))
  ) {
    return null;
  }
  return varName;
}

function matchKeys(queryKey, routeKey) {
  const routeKeyIsVar = isString(routeKey) &&
    routeKey.startsWith(OPEN_VAR) &&
    routeKey.endsWith(CLOSE_VAR);
  const routeKeyIsList = isArray(routeKey);

  if (routeKeyIsVar) {
    const varName = matchAgainstVariable(queryKey, routeKey);
    if (varName === null) {
      return { matched: false };
    }
    return { matched: true, varName };
  } else if (routeKeyIsList) {
    if (!routeKey.includes(queryKey)) {
      return { matched: false };
    }
    return { matched: true, varName: '' };
  } else if (queryKey !== routeKey) {
    return { matched: false };
  }
  return { matched: true };
}

function findMatches(queryPathExpression, routePathExpression, path) {
  const matches = { 0: path, $path: path };
  let paramIndex = 1;

  for (let i = 0; i < routePathExpression.length; i++) {
    const queryKey = queryPathExpression[i];
    const routeKey = routePathExpression[i];

    const isLast = i === routePathExpression.length - 1;
    if (isLast && isWildCardKey(routeKey)) {
      matches[WILDCARD] = matches[paramIndex++] = join(queryPathExpression.slice(i));
      break;
    }

    const matchResult = matchKeys(queryKey, routeKey);
    if (matchResult.matched === false) {
      return false;
    } else if (matchResult.varName != null) {
      matches[paramIndex++] = queryKey;
      if (matchResult.varName) {
        matches[matchResult.varName] = queryKey;
      }
    }
  }

  return matches;
}

function countVariables(route) {
  const varExp = new RegExp(`\\${OPEN_VAR}|\\${WILDCARD}`, 'g');
  const varMatches = route.match(varExp);
  return (varMatches || []).length;
}

function sortMatches({ routePath: route1 }, { routePath: route2 }) {
  const route1Array = escapeToPathSyntax(route1);
  const route2Array = escapeToPathSyntax(route2);
  if (route1Array.length === route2Array.length) {
    const varCount1 = countVariables(route1);
    const varCount2 = countVariables(route2);
    return varCount1 - varCount2;
  }
  return route2Array.length - route1Array.length;
}

/**
 * Match a query path against a route expression
 * @param {LeafPath} queryPath the path to match
 * @param {RoutePathSet} routePath the route expression to match against
 * @returns {Object | boolean} returns `false` if the route does not match, otherwise
 * returns any resolved parameters
 * @example
 * match('fx.pairs[1].name', 'fx.pairs[{integers:index}].{keys:key}');
 * // => { index: 1, key: 'name' }
 * match('fx.list[20].bid', 'fx.pairs[{integers:index}].{keys:key}');
 * // => false
 */
function match(queryPath, routePath) {
  if (routePath === undefined) {
    return (curriedRoutePath) => match(queryPath, curriedRoutePath);
  }

  const queryPathExpression = escapeToPathSyntax(queryPath);
  const routePathExpression = escapeToPathSyntax(routePath);

  if (!matchesLengthOrWildcard(queryPathExpression, routePathExpression)) {
    return false;
  }

  return findMatches(queryPathExpression, routePathExpression, queryPath);
}

/**
 * Find all matching routes for a query path from an array of route expressions
 * @param {LeafPath} path the path to match
 * @param {Array<RoutePathSet>} routes the set of route expressions to match against
 * @returns {Array.<RoutePathAndParams>} the matching routes and any resolved parameters
 * @example
 * routeMatch('fx.pairs[1].name', [
 *   'fx.*',
 *   'fx.pairs[{integers:index}].["bid","ask"]',
 *   'fx.pairs[{integers:index}].{keys:key}',
 *   'ui.*',
 * );
 * // =>
 * [
 *   { routePath: 'fx.*', params: { '*': 'pairs[1].name' } },
 *   { routePath: 'fx.pairs[{integers:index}].{keys:key}', params: { index: 1, key: 'name' } },
 * ]
 */
function routeMatch(path, routes = /* istanbul ignore next */ []) {
  const matcher = match(path);
  return routes
    .map((route) => ({ routePath: route, params: matcher(route) }))
    .filter(({ params }) => !!params);
}

/**
 * Return a list of sorted matches for a query path from an array of route expressions
 * @param {LeafPath} path the path to match
 * @param {Array<RoutePathSet>} routes the set of route expressions to match against
 * @returns {Array.<RoutePathAndParams>} the matching routes and any resolved parameters,
 * sorted by relevance
 * @example
 * sortedMatches('fx.pairs[1].name', [
 *   'fx.*',
 *   'fx.pairs[{integers:index}].["bid","ask"]',
 *   'fx.pairs[{integers:index}].{keys:key}',
 *   'ui.*',
 * );
 * // =>
 * [
 *   { routePath: 'fx.pairs[{integers:index}].{keys:key}', params: { index: 1, key: 'name' } },
 *   { routePath: 'fx.*', params: { '*': 'pairs[1].name' } },
 * ]
 */
function sortedMatches(path, routes) {
  const matches = routeMatch(path, routes);
  matches.sort(sortMatches);
  return matches;
}

/**
 * Find the most appropriate match for a leaf path from a list of route expressions
 * @param {LeafPath} path the path to match
 * @param {Array<RoutePathSet>} routes the set of route expressions to match against
 * @returns {RoutePathAndParams} the best matching route and any resolved parameters
 * @example
 * topMatch('fx.pairs[1].name', [
 *   'fx.pairs[{integers:index}].["bid","ask"]',
 *   'fx.pairs[{integers:index}].{keys:key}',
 *   'fx.*',
 *   'ui.*',
 * );
 * // => { routePath: 'fx.pairs[{integers:index}].{keys:key}', params: { index: 1, key: 'name' } }
 */
function topMatch(path, routes) {
  const matches = sortedMatches(path, routes);
  return matches[0];
}

module.exports = {
  match,
  routeMatch,
  topMatch,
  sortedMatches,
};
